import { Call, Device } from '@twilio/voice-sdk'
import { telephonyApi } from './api'
import { callMachine } from './callMachine'
import { UNREACHABLE_DIAL_FAILURE } from './dialFailure'
import { currentMicBrowser } from './micBrowser'
import {
  adoptLegacyMicGrant,
  decideMicRequest,
  forgetMicGrant,
  hasMicGrantMemo,
  rememberMicGrant,
  watchMicPermission,
  type MicPermissionState,
  type MicPermissionWatch,
} from './micPermission'

/**
 * Wrapper fin du Twilio Voice SDK — v2.
 *
 * Responsabilités STRICTEMENT limitées :
 * - cycle de vie du Device (init, register, destroy, refresh de token)
 * - survie du poste sur iPad/Safari (veille/réveil, micro, AudioContext)
 * - connect() sortant avec le callId en custom parameter
 * - accept/reject des entrants
 * - traduction des événements SDK → actions callMachine + abonnés
 *
 * Aucune logique métier ici (pas d'accès DB/REST hors token, pas d'outcome,
 * pas de permission CRM, pas de polling de stats).
 *
 * Acquis v1 portés depuis `services/twilioVoiceSimple.ts` (LECTURE SEULE) :
 * garde d'init concurrente, edge Dublin + ICE restart, refresh de token
 * proactif ET sur `tokenWillExpire`, reprise après veille iPad (micro +
 * AudioContext + re-register), sonnerie locale pendant le dial, sets
 * d'abonnés multiples, typage des erreurs micro/token.
 */

// ---------------------------------------------------------------------------
// Types publics
// ---------------------------------------------------------------------------

/** État du poste, tel que l'UI peut l'afficher (badge, bannière). */
export type TelephonyDeviceState =
  | 'uninitialized'
  | 'initializing'
  /**
   * Issue #190 — le poste ATTEND UN GESTE de l'agent avant de demander le
   * micro : le navigateur afficherait sinon sa demande par surprise au
   * chargement, et un « Refuser » réflexe est définitif sur Chromium. Aucun
   * Device n'existe dans cet état ; `requestMicrophone()` en sort.
   */
  | 'awaiting-mic'
  | 'registered'
  | 'unregistered'
  | 'error'

/**
 * Famille d'erreur exploitable par l'UI sans connaître les codes Twilio.
 * `mic` = action utilisateur possible (autoriser le micro) ; `token` = session
 * expirée (refresh tenté automatiquement) ; `network` = transport.
 */
export type TelephonyErrorKind = 'mic' | 'token' | 'network' | 'unknown'

/**
 * Issue #190 — CE QUI NE VA PAS avec le micro. Les trois causes appellent
 * trois gestes différents de l'agent ; les confondre sous « micro refusé »
 * l'envoyait dans les réglages du navigateur pour un casque débranché.
 *
 *  - `denied`      : le navigateur (ou le système) refuse l'accès ;
 *  - `not-found`   : aucun micro n'est branché ;
 *  - `busy`        : le micro existe mais une autre application le tient ;
 *  - `unavailable` : échec média rapporté par le SDK sans cause lisible.
 */
export type TelephonyMicIssue = 'denied' | 'not-found' | 'busy' | 'unavailable'

export interface TelephonyDeviceError {
  kind: TelephonyErrorKind
  /** Précision de la cause — présente si et seulement si `kind === 'mic'`. */
  mic?: TelephonyMicIssue
  /** Code Twilio brut quand il existe (31201, 31204, 31205…). */
  code?: number
  message: string
}

export interface TelephonyDeviceStatus {
  state: TelephonyDeviceState
  /** Dernière erreur non résolue. Remise à null dès que le poste redevient sain. */
  error: TelephonyDeviceError | null
}

/** Événements diffusés aux abonnés (API subscribe → unsubscribe). */
export interface TelephonyDeviceEventMap {
  incoming: (call: Call) => void
  accept: (call: Call) => void
  disconnect: (call: Call) => void
  /**
   * Le leg en cours a perdu sa signalisation ou son média et le SDK tente de
   * le rétablir (`reconnecting` / `reconnected` du SDK). La machine à états ne
   * connaît pas cet état intermédiaire — et n'a pas à le connaître : l'appel
   * n'est ni fini ni sain. Le wrapper le diffuse tel quel pour qu'une UI
   * puisse afficher « reconnexion… » ; sans abonné, il reste un journal.
   */
  reconnecting: (call: Call) => void
  reconnected: (call: Call) => void
  error: (error: TelephonyDeviceError) => void
  status: (status: TelephonyDeviceStatus) => void
  tokenRefreshed: () => void
  /**
   * Volume d'écoute (0 → 1). Diffusé pour que le CONTRÔLE puisse s'y abonner
   * seul : sans lui, la valeur vivait dans un `useState` de la page, et chaque
   * cran du curseur re-rendait la scène ENTIÈRE — fiche, script et onglets —
   * en pleine conversation (mineur e de la review de #47, contraire à la
   * priorité fluidité).
   */
  volume: (level: number) => void
}

export type TelephonyDeviceEvent = keyof TelephonyDeviceEventMap

// ---------------------------------------------------------------------------
// Constantes
// ---------------------------------------------------------------------------

/** TTL par défaut d'un token Twilio (le backend renvoie le vrai `ttl`). */
const DEFAULT_TOKEN_TTL_SECONDS = 3600

/** Marge de refresh AVANT expiration (v1 : 50 min pour un token de 60 min). */
const TOKEN_REFRESH_MARGIN_SECONDS = 600

/** Plancher de sécurité : jamais un intervalle de refresh sous une minute. */
const MIN_TOKEN_REFRESH_MS = 60_000

/**
 * Durée d'invisibilité au-delà de laquelle on considère que l'iPad a dormi.
 * Aligné sur `WAKE_UP_THRESHOLD_MS` de `services/api.ts` (même phénomène).
 * En dessous, un simple aller-retour d'onglet ne déclenche pas de reprise —
 * SAUF si un appel est en cours (Safari peut couper le micro très vite).
 */
const WAKE_UP_THRESHOLD_MS = 5_000

/** Codes Twilio « micro / media » → l'agent peut agir (autoriser le micro). */
const MIC_ERROR_CODES = new Set([31201, 31208])

/** Codes Twilio « token » → refresh immédiat, transparent pour l'agent. */
const TOKEN_ERROR_CODES = new Set([31204, 31205])

/**
 * Attente BORNÉE d'un enregistrement DÉJÀ EN VOL (jamais un retry programmé).
 *
 * Le SDK refuse `register()` quand un enregistrement est en cours ; on attend
 * alors son issue. Cette borne n'existe que pour éviter qu'une reprise reste
 * bloquée à jamais et gèle la garde de reprise unique — sans elle, un
 * enregistrement qui n'aboutit pas condamnerait toutes les reprises suivantes.
 */
const REGISTER_WAIT_TIMEOUT_MS = 10_000

/**
 * CHIEN DE GARDE d'un appel en RECONNEXION (B-01, « poste zombie »).
 *
 * Aligné sur `BACKOFF_CONFIG.max` du SDK (`@twilio/voice-sdk` `call.js:24`,
 * 30 000 ms) : au-delà, le SDK abandonne lui-même la reconnexion MÉDIA
 * (« Exceeded max ICE retries ») et clôt le leg. La reconnexion de
 * SIGNALISATION, elle, n'a AUCUNE borne côté SDK : un leg peut rester
 * `reconnecting` indéfiniment si le WebSocket ne revient jamais. Ce délai est
 * le seul filet — il ne se déclenche que si ni `reconnected` ni `disconnect`
 * ne sont arrivés entre-temps (ce n'est pas un retry, c'est un constat d'échec).
 */
const CALL_RECONNECT_WATCHDOG_MS = 30_000

/**
 * Valeur de `call.status()` une fois le leg définitivement clos (`Call.State.Closed`
 * du SDK — enum de chaînes). Comparée en chaîne : l'enum n'est pas comparable à
 * un littéral sous TypeScript, et la doublure du SDK des specs n'expose pas
 * l'objet `Call.State`.
 */
const CALL_STATUS_CLOSED = 'closed'
const CALL_STATUS_RECONNECTING = 'reconnecting'

/** Sonnerie française : 440 Hz, 1,5 s de tonalité puis 3,5 s de silence. */
const RINGBACK_FREQUENCY_HZ = 440
const RINGBACK_ON_SECONDS = 1.5
const RINGBACK_CYCLE_SECONDS = 5
const RINGBACK_VOLUME = 0.15

/**
 * Niveau du flux DISTANT (`volume` du SDK, 0 → 1) au-delà duquel on considère
 * qu'il y a réellement quelque chose à entendre en face — early media de
 * l'opérateur, annonce, ou voix.
 *
 * Choisi juste au-dessus du plancher de bruit d'un flux ouvert mais muet, et
 * très en dessous d'une tonalité réelle : il faut couper vite (la superposition
 * s'entend immédiatement) sans couper sur du silence.
 */
const REMOTE_AUDIO_VOLUME_THRESHOLD = 0.02

/**
 * `InvalidStateError` du SDK : « on te demande une transition que ton état
 * courant n'autorise pas » (register alors qu'un register est en vol,
 * unregister sur un Device qui ne l'est plus…).
 *
 * Ce n'est jamais la panne d'un poste : c'est une COURSE. On la distingue donc
 * des vraies erreurs, qui, elles, condamnent la session.
 */
function isInvalidStateError(error: unknown): boolean {
  return (error as { name?: string } | null)?.name === 'InvalidStateError'
}

/**
 * Une erreur DÉSIGNE-T-ELLE LE POSTE lui-même ?
 *
 * Seules le micro (l'agent doit autoriser) et le jeton (session à renouveler)
 * disent quelque chose sur l'état du poste. Les erreurs de transport
 * (`network`, `unknown`) portées par un appel disent seulement que CET APPEL a
 * échoué — un 31005 « hangup » de la passerelle sur un numéro invalide en est
 * l'exemple type.
 *
 * C'est cette distinction qui empêche le bandeau « Connexion au service
 * téléphonique perdue » de s'allumer sur un simple mauvais numéro : le statut
 * du Device ne reçoit plus que ce qui le concerne.
 */
function isDeviceScopedError(kind: TelephonyErrorKind): boolean {
  return kind === 'mic' || kind === 'token'
}

// ---------------------------------------------------------------------------
// Sonnerie locale (feedback audio pendant que Twilio établit le sortant)
// ---------------------------------------------------------------------------

/**
 * Tonalité de retour d'appel jouée localement entre `connect()` et le premier
 * signal audio de Twilio. Sans elle, l'agent a 1 à 3 s de silence total et
 * croit que l'appel n'est pas parti (constat v1).
 *
 * Le rythme est programmé par l'horloge Web Audio (`setTargetAtTime`), pas par
 * des `setTimeout` imbriqués comme en v1 : un seul timer, aucune dérive.
 */
class LocalRingback {
  private oscillator: OscillatorNode | null = null
  private gain: GainNode | null = null
  private cycleTimer: ReturnType<typeof setInterval> | null = null

  get isPlaying(): boolean {
    return this.oscillator !== null
  }

  start(context: AudioContext): void {
    if (this.isPlaying) return

    try {
      const gain = context.createGain()
      gain.gain.value = 0
      gain.connect(context.destination)

      const oscillator = context.createOscillator()
      oscillator.type = 'sine'
      oscillator.frequency.value = RINGBACK_FREQUENCY_HZ
      oscillator.connect(gain)
      oscillator.start()

      this.gain = gain
      this.oscillator = oscillator

      const cycle = () => {
        if (!this.gain) return
        const now = context.currentTime
        this.gain.gain.setTargetAtTime(RINGBACK_VOLUME, now, 0.02)
        this.gain.gain.setTargetAtTime(0, now + RINGBACK_ON_SECONDS, 0.02)
      }

      cycle()
      this.cycleTimer = setInterval(cycle, RINGBACK_CYCLE_SECONDS * 1000)
    } catch (error) {
      // Une sonnerie muette ne doit jamais empêcher un appel de partir.
      console.warn('[telephony-v2] Sonnerie locale indisponible', error)
      this.stop()
    }
  }

  stop(): void {
    if (this.cycleTimer) {
      clearInterval(this.cycleTimer)
      this.cycleTimer = null
    }
    if (this.oscillator) {
      try {
        this.oscillator.stop()
        this.oscillator.disconnect()
      } catch {
        // oscillateur déjà arrêté — sans conséquence
      }
      this.oscillator = null
    }
    if (this.gain) {
      this.gain.disconnect()
      this.gain = null
    }
  }
}

// ---------------------------------------------------------------------------
// Device
// ---------------------------------------------------------------------------

class TelephonyDevice {
  private device: Device | null = null
  private activeCall: Call | null = null
  private initializing: Promise<void> | null = null

  private status: TelephonyDeviceStatus = { state: 'uninitialized', error: null }

  /** Un set d'abonnés par événement : aucun composant n'écrase les autres. */
  private subscribers: {
    [K in TelephonyDeviceEvent]: Set<TelephonyDeviceEventMap[K]>
  } = {
    incoming: new Set(),
    accept: new Set(),
    disconnect: new Set(),
    reconnecting: new Set(),
    reconnected: new Set(),
    error: new Set(),
    status: new Set(),
    tokenRefreshed: new Set(),
    volume: new Set(),
  }

  private tokenRefreshTimer: ReturnType<typeof setInterval> | null = null

  /**
   * Chien de garde de la reconnexion du leg ACTIF (cf. `CALL_RECONNECT_WATCHDOG_MS`).
   * Un seul, parce qu'un seul leg à la fois ; désarmé dès que le leg est
   * relâché, quel que soit le chemin.
   */
  private reconnectWatchdog: ReturnType<typeof setTimeout> | null = null

  private audioContext: AudioContext | null = null
  private ringback = new LocalRingback()

  /**
   * VOLUME D'ÉCOUTE (0 → 1), mémorisé D'UN APPEL À L'AUTRE.
   *
   * Le réglage appartient à l'agent et à son casque, pas à un appel : le
   * baisser une fois puis le retrouver au niveau maximum au contact suivant
   * serait un réglage à refaire deux cents fois par jour.
   */
  private outputVolume = 1

  /**
   * Éléments `<audio>` par lesquels le SDK fait entendre le correspondant.
   *
   * ⚠️ **POURQUOI PAS `connection.volume(v)` (le portage littéral de la v1).**
   * `twilioVoiceSimple.ts:840` appelle `this.currentConnection.volume(x)`
   * derrière un `typeof … === 'function'`. Or dans le SDK 2.x, `volume` n'est
   * pas une méthode de `Call` : c'est un ÉVÉNEMENT (`call.on('volume', (in,
   * out) => …)`, cf. `call.d.ts`). Le garde-fou de la v1 rendait donc l'appel
   * silencieusement sans effet — **le réglage de volume v1 ne faisait rien**,
   * et le porter tel quel n'aurait rien fait non plus.
   *
   * Le SDK expose en revanche un événement PUBLIC et documenté qui donne
   * l'élément de lecture lui-même : `call.on('audio', (remoteAudio:
   * HTMLAudioElement) => …)`. On le collecte, et le volume se règle là où il
   * se règle vraiment — sur l'élément média. Aucune API interne n'est touchée.
   */
  private remoteAudio = new Set<HTMLAudioElement>()

  private hiddenAt: number | null = null
  private visibilityBound = false

  /**
   * UNE seule reprise du poste à la fois, QUEL QUE SOIT son déclencheur —
   * réveil d'iPad ou erreur du Device.
   *
   * Deux reprises concurrentes se marchaient dessus : celle qui appelait
   * `register()` pendant que l'autre enregistrait déjà recevait un
   * `InvalidStateError` du SDK, le prenait pour un poste hors service, et
   * condamnait à tort l'appel en cours. Une seule file, un seul verdict.
   */
  private recovery: Promise<void> | null = null

  /**
   * Jeton d'ÉPOQUE du poste. Incrémenté par `destroy()` : une initialisation
   * lancée avant la destruction et qui se termine après elle est PÉRIMÉE — elle
   * n'assigne aucun Device et ne démarre aucun timer (sinon : Device fantôme
   * enregistré chez Twilio, intervalle de refresh qui fuit, et une deuxième
   * sonnerie possible sur les entrants).
   */
  private epoch = 0

  /**
   * Issue #190 — veille de la permission micro (`navigator.permissions`),
   * ouverte à la première initialisation et tenue jusqu'à `destroy()`.
   *
   * Elle sert à deux choses : lire l'état AVANT de demander le micro (pour ne
   * pas faire surgir la demande du navigateur au chargement), et reprendre
   * toute seule quand l'agent autorise le micro dans les réglages du site
   * (`onchange` → `granted`). La référence est GARDÉE : un `PermissionStatus`
   * ramassé par le GC n'émet plus rien.
   */
  private micWatch: Promise<MicPermissionWatch> | null = null

  // -------------------------------------------------------------------------
  // Abonnements (API subscribe → unsubscribe)
  // -------------------------------------------------------------------------

  /**
   * S'abonner à un événement du poste. Retourne la fonction de désabonnement —
   * à appeler au cleanup du composant. Les sets sont indépendants du cycle de
   * vie du Device : un re-register ne ré-attache aucun écouteur, donc aucune
   * fuite ni double notification.
   */
  subscribe<K extends TelephonyDeviceEvent>(
    event: K,
    handler: TelephonyDeviceEventMap[K],
  ): () => void {
    const set = this.subscribers[event] as Set<TelephonyDeviceEventMap[K]>
    set.add(handler)
    return () => {
      set.delete(handler)
    }
  }

  private emit<K extends TelephonyDeviceEvent>(
    event: K,
    ...args: Parameters<TelephonyDeviceEventMap[K]>
  ): void {
    // Copie : un abonné qui se désabonne pendant la diffusion ne casse pas l'itération.
    const handlers = Array.from(this.subscribers[event]) as Array<(...a: unknown[]) => void>
    handlers.forEach((handler) => {
      try {
        handler(...(args as unknown[]))
      } catch (error) {
        console.error(`[telephony-v2] Abonné "${event}" en erreur`, error)
      }
    })
  }

  getStatus(): TelephonyDeviceStatus {
    return this.status
  }

  private setStatus(state: TelephonyDeviceState, error: TelephonyDeviceError | null = null): void {
    if (this.status.state === state && this.status.error?.message === error?.message) return
    this.status = { state, error }
    this.emit('status', this.status)
  }

  // -------------------------------------------------------------------------
  // Cycle de vie
  // -------------------------------------------------------------------------

  /**
   * Garde d'init concurrente (leçon v1 : double init = double sonnerie).
   *
   * `askMicNow` (issue #190) : l'appelant garantit que demander le micro
   * MAINTENANT est légitime — un geste de l'agent (`requestMicrophone`,
   * `ensureReady` depuis un clic d'appel) ou une permission qui vient d'être
   * accordée. Sans lui (chargement de l'application), le poste consulte
   * d'abord la permission et peut s'arrêter en `awaiting-mic`.
   */
  async init(options: { askMicNow?: boolean } = {}): Promise<void> {
    if (this.device) return
    if (this.initializing) return this.initializing
    // La garde ne se relâche que pour SON initialisation : un `destroy()` suivi
    // d'un nouvel `init()` ne doit pas voir sa garde effacée par la fin de
    // l'initialisation précédente (ce serait la porte ouverte à deux Devices).
    const pending: Promise<void> = this.doInit(options.askMicNow === true).finally(() => {
      if (this.initializing === pending) this.initializing = null
    })
    this.initializing = pending
    return pending
  }

  private async doInit(askMicNow: boolean): Promise<void> {
    // Époque de CETTE initialisation. `destroy()` l'invalide : chaque reprise
    // après `await` vérifie qu'elle a toujours cours avant d'agir.
    const epoch = this.epoch
    const isStale = () => this.epoch !== epoch

    this.setStatus('initializing')

    try {
      // 0) Issue #190 — NE PAS faire surgir la demande de micro au chargement.
      //    Sur un geste, on demande tout de suite (comme avant) et la veille
      //    s'ouvre en parallèle. Sinon on lit d'abord la permission : déjà
      //    accordée → rien ne change ; à demander → on attend le geste.
      const micWatch = this.ensureMicWatch()
      if (!askMicNow) {
        const permission = (await micWatch).read()
        if (isStale()) return
        const { engine } = currentMicBrowser()
        // Premier chargement après le déploiement du lot : un iPad qui a déjà
        // servi au téléphone hérite du mémo (cf. `adoptLegacyMicGrant`) —
        // sinon tout un plateau verrait le pré-écran le même matin.
        if (engine === 'webkit') adoptLegacyMicGrant()
        const decision = decideMicRequest({
          permission,
          engine,
          grantedBefore: hasMicGrantMemo(),
        })
        if (decision === 'await-gesture') {
          this.setStatus('awaiting-mic')
          return
        }
      }

      // 1) Micro AVANT le Device : un refus remonte ici en erreur typée
      //    (`NotAllowedError`) plutôt qu'en 31201 opaque au premier appel.
      await this.warmMicrophone()
      if (isStale()) return

      // 2) Token
      const { token, ttl } = await telephonyApi.getToken()
      if (isStale()) return

      // 3) Device — options héritées de la v1 (Safari/iPad, Europe).
      const device = new Device(token, {
        logLevel: 'warn',
        codecPreferences: ['opus', 'pcmu'] as Call.Codec[],
        // Edge européen : latence divisée par ~2 depuis la France/Israël.
        edge: 'dublin',
        // NB : la v1 passe aussi `enableIceRestart: true` (sous un cast `as any`).
        // L'option a été RETIRÉE du SDK 2.x — le redémarrage ICE y est toujours
        // actif. La reprise de connexion après veille est donc bien portée, mais
        // par le SDK lui-même : la repasser ne ferait qu'ajouter un cast inutile.
      })

      this.bindDeviceEvents(device, epoch)

      await device.register()

      if (isStale()) {
        // Le poste a été démonté pendant l'enregistrement : ce Device est
        // orphelin. Le laisser vivre, c'est un second client enregistré chez
        // Twilio pour la même identité — donc une sonnerie en double.
        device.destroy()
        return
      }

      this.device = device
      this.setStatus('registered')
      this.startTokenRefresh(ttl)
      this.bindVisibilityHandler()
    } catch (error) {
      if (isStale()) return
      const typed = this.toDeviceError(error)
      this.setStatus('error', typed)
      this.emit('error', typed)
      throw error
    }
  }

  private bindDeviceEvents(device: Device, epoch: number): void {
    /**
     * Un Device abandonné (époque révolue, destruction en cours) continue
     * d'émettre : `destroy()` du SDK provoque lui-même un `unregistered`, et un
     * enregistrement en vol peut aboutir bien après. Sans ce recoupement, ces
     * événements écrasaient l'état du poste COURANT — un « déconnecté » ou une
     * erreur venus d'un mort.
     *
     * `this.device === null` reste accepté tant que l'époque tient : c'est la
     * fenêtre normale de `doInit`, entre le binding et l'installation.
     */
    const isCurrent = (): boolean =>
      this.epoch === epoch && (this.device === null || this.device === device)

    device.on('registered', () => {
      if (!isCurrent()) return
      this.setStatus('registered')
    })

    device.on('unregistered', () => {
      if (!isCurrent()) return
      // Pas de reconnexion par minuterie (interdit par le cahier) : l'état
      // remonte à l'UI, et la reprise se fait au retour de veille, sur erreur
      // du Device (`recoverDevice`) ou à la prochaine action de l'agent
      // (`ensureReady`, appelé par `connect()`).
      console.warn('[telephony-v2] Device désenregistré')
      this.setStatus('unregistered')
    })

    device.on('tokenWillExpire', () => {
      if (!isCurrent()) return
      void this.refreshToken()
    })

    device.on('error', (error, call?: Call) => {
      if (!isCurrent()) return
      const typed = this.toDeviceError(error)

      // ⚠️ M2 — IDENTITÉ DU LEG. Le SDK passe en 2e argument le Call concerné
      // quand l'erreur en vise un. Une erreur portant sur un leg que le poste
      // ne tient plus (démontage du PRÉCÉDENT appel, typiquement 31000) ne doit
      // toucher NI l'état d'appel NI le statut du poste : c'est du bruit de
      // fin de vie. Sans ce recoupement, elle arrivait pile au moment où
      // l'entrant suivant était présenté et le faisait disparaître de l'écran
      // (famille F-8). Même rigueur `wasLive` que les handlers de leg.
      if (call && this.activeCall !== call) {
        console.warn(
          '[telephony-v2] Erreur Device sur un leg déjà relâché — ignorée',
          {
            kind: typed.kind,
            code: typed.code,
            staleSid: call.parameters?.CallSid,
            heldSid: this.activeCall?.parameters?.CallSid ?? null,
          },
        )
        return
      }

      /**
       * ⚠️ ÉCHEC DU LEG ≠ PANNE DU POSTE (constat de recette du 2026-07-28).
       *
       * Une erreur de TRANSPORT portée par l'appel en cours, AVANT tout
       * décroché, ne dit rien de la santé du Device : elle dit que ce numéro
       * n'aboutit pas. Un 31005 sur un numéro invalide passait par ici et
       * allumait le bandeau « Connexion au service téléphonique perdue », puis
       * déclenchait une reprise complète du poste — pour un mauvais numéro.
       *
       * On la traite donc comme un ÉCHEC D'APPEL : ni `setStatus('error')`, ni
       * `recoverFromError`. La machine décide de la suite (qualification en
       * campagne, composeur en manuel).
       */
      if (call && !isDeviceScopedError(typed.kind)) {
        const { phase } = callMachine.getSnapshot()
        if (phase === 'dialing' || phase === 'ringing') {
          console.warn(
            '[telephony-v2] Échec du leg pendant la numérotation — classé ÉCHEC D’APPEL (le poste n’est pas en cause)',
            { kind: typed.kind, code: typed.code, message: typed.message, phase },
          )
          this.emit('error', typed)
          this.endLiveCallOnError(typed, { legFailure: true })
          return
        }
      }

      console.error('[telephony-v2] Device error', typed)
      this.setStatus('error', typed)
      this.emit('error', typed)

      // Refresh immédiat sur erreur de token : l'agent ne voit rien passer.
      // Le refresh remet lui-même le statut au propre s'il aboutit.
      if (typed.kind === 'token') {
        void this.refreshToken()
        return
      }

      // Erreur MICRO : TERMINALE jusqu'à une action de l'agent (autoriser le
      // micro dans Safari). Re-enregistrer n'y changerait rien — le poste doit
      // rester visiblement en erreur pour que la bannière soit affichée. Le
      // chemin de retour existe : au réveil comme à la prochaine action
      // (`ensureReady`), `recoverDevice` REDEMANDE le micro et ne rend
      // `registered` que s'il est réellement retrouvé.
      if (typed.kind === 'mic') {
        if (call) this.endLiveCallOnError(typed)
        return
      }

      // ⚠️ M1 — une erreur NON FATALE n'est plus un état terminal. Une coupure
      // de transport passagère laissait le poste en `error` — donc l'agent
      // marqué indisponible en base — jusqu'au prochain rechargement de page :
      // un poste sourd sans que personne ne le sache. On tente UN
      // rétablissement, déclenché par l'événement d'erreur (pas de minuterie) ;
      // au retour à `registered`, le provider republie la disponibilité.
      //
      // Le leg éventuellement vivant, lui, est perdu : il est qualifié tout de
      // suite (wrapup), avant même de savoir si le poste se rétablit.
      if (call) this.endLiveCallOnError(typed)
      void this.recoverFromError(typed)
    })

    device.on('incoming', (call: Call) => {
      if (!isCurrent()) {
        // Un Device abandonné qui reçoit encore un entrant : le refuser rend
        // la main à Twilio tout de suite (re-routage), alors que l'ignorer
        // ferait sonner 25 s dans le vide.
        call.reject()
        return
      }
      // Le poste croit encore tenir un appel : accepter ce leg écraserait la
      // référence et rendrait l'appel en cours impossible à raccrocher.
      if (this.activeCall) {
        console.warn(
          '[telephony-v2] Entrant refusé : un leg est déjà tenu par le poste',
          { heldSid: this.activeCall.parameters?.CallSid },
        )
        call.reject()
        return
      }
      const from = call.parameters.From ?? 'Inconnu'
      // C'est la MACHINE qui arbitre (elle seule connaît la phase courante et
      // ses règles). Un refus se traduit par un `reject()` explicite : Twilio
      // reprend la main tout de suite et re-route vers un autre agent, au lieu
      // de faire sonner 25 s dans le vide un poste qui ne répondra pas.
      const presented = callMachine.incomingRinging({
        twilioSid: call.parameters.CallSid,
        phoneNumber: from,
        // Posé par le TwiML <Parameter name="callId"> côté backend
        callId: call.customParameters?.get('callId'),
      })
      if (!presented) {
        call.reject()
        return
      }
      this.activeCall = call
      this.bindCallEvents(call)
      this.emit('incoming', call)
    })
  }

  /**
   * Fin d'un appel VIVANT tué par une erreur SDK.
   *
   * L'appel a-t-il été une conversation ? Si oui, il est QUALIFIABLE : le
   * renvoyer en `idle` sec ferait perdre le résultat d'un appel réel, et
   * l'exclusion de wrapup côté backend le tiendrait quand même à l'écart du
   * routage pendant 3 minutes. On passe donc par `wrapup` (needsOutcome), en
   * emportant le message d'erreur pour qu'il reste affichable.
   *
   * Avant décroché (dialing/ringing/incoming), il n'y a rien à qualifier :
   * `fail()` remet le poste au repos en conservant l'erreur.
   *
   * `legFailure` marque le cas particulier de l'échec de NUMÉROTATION (le leg
   * n'a jamais abouti) : la décision revient alors entièrement à la machine,
   * qui sait seule si l'appel appartient à une campagne — cf. `dialFailed()`.
   */
  private endLiveCallOnError(
    error: TelephonyDeviceError,
    opts: { legFailure?: boolean } = {},
  ): void {
    const call = this.activeCall
    const { phase, callId } = callMachine.getSnapshot()

    if (call) {
      try {
        // ⚠️ Le média peut être ENCORE VIVANT : beaucoup d'erreurs du SDK sont
        // des erreurs de SIGNALISATION, et le rétablissement peut aussi rendre
        // un faux négatif. Relâcher la référence sans raccrocher laisserait
        // l'appel Twilio en cours SANS aucune UI pour y mettre fin : l'agent
        // reste en ligne à son insu, et l'appelant l'entend.
        //
        // Un ENTRANT qui sonne se REFUSE plutôt qu'il ne se raccroche : Twilio
        // re-route immédiatement vers un autre agent (même raison qu'au
        // `reject()` de la machine), au lieu de sonner 25 s dans le vide.
        if (phase === 'incoming') call.reject()
        else call.disconnect()
      } catch (hangupError) {
        // Le leg était bel et bien mort : rien à raccrocher, on continue.
        console.warn(
          '[telephony-v2] Raccrochage du leg en erreur impossible',
          hangupError,
        )
      }
    }

    // Le leg est perdu : le RELÂCHER est indispensable. Tant que le poste croit
    // tenir un appel, il refuse tout entrant (« un leg est déjà tenu ») — un
    // état résiduel dont l'agent ne peut plus sortir sans recharger la page.
    // Appelé aussi depuis `call.on('error')`, où le leg est déjà relâché :
    // l'opération est alors sans effet.
    this.releaseCall()

    /**
     * ÉCHEC DE NUMÉROTATION : c'est la machine qui tranche, et elle seule.
     *
     * `dialFailed()` est un NO-OP si l'échec a déjà été classé (statut backend
     * arrivé le premier) : le poste ne peut donc plus écraser un `wrapup`
     * ouvert, ni remplacer un message précis (« Numéro occupé ») par un message
     * générique. C'était l'autre moitié du gel constaté en recette.
     */
    if (opts.legFailure && phase !== 'active' && phase !== 'incoming') {
      // `dialing`/`ringing` : la machine classe. `wrapup`/`idle` : l'échec a
      // déjà été classé ailleurs et `dialFailed()` ne touche à rien — c'est
      // précisément ce no-op qui protège l'état déjà décidé.
      // `active` et `incoming` sont exclus : un leg qui meurt en conversation
      // (ou un entrant qui sonne) n'est pas un échec de NUMÉROTATION, il suit
      // le chemin nominal ci-dessous.
      callMachine.dialFailed(UNREACHABLE_DIAL_FAILURE)
      return
    }

    // CLÉ i18n, jamais le message technique : `CallState.error` est traduit par
    // la scène (le message du SDK, lui, reste dans le journal ci-dessus).
    const errorKey = `device.error.${error.kind}`
    if (phase === 'active' && callId) {
      callMachine.disconnected({ needsOutcome: true, error: errorKey })
      return
    }
    callMachine.fail(errorKey)
  }

  /**
   * Tentative UNIQUE de rétablissement après une erreur non fatale du poste.
   *
   * Déclenchée par l'événement d'erreur lui-même — jamais programmée : le
   * cahier interdit les chaînes de retry par minuterie (la v1 en avait deux).
   * Si le rétablissement aboutit, le retour à `registered` fait republier la
   * disponibilité par le provider ; sinon le poste RESTE en erreur, et un appel
   * encore en cours est clos (le Device est alors réellement hors service).
   *
   * Le verdict est rendu APRÈS la reprise partagée : si une reprise de veille
   * était déjà en vol, on attend la sienne au lieu d'en lancer une concurrente
   * (c'est cette concurrence qui produisait des faux négatifs).
   */
  private async recoverFromError(error: TelephonyDeviceError): Promise<void> {
    const epoch = this.epoch
    await this.recoverDevice()
    if (this.epoch !== epoch) return

    if (this.status.state === 'registered') {
      console.warn('[telephony-v2] Poste rétabli après erreur', {
        kind: error.kind,
        code: error.code,
      })
      return
    }

    console.error('[telephony-v2] Rétablissement impossible — poste hors service')
    this.setStatus('error', error)
    // Le Device est bel et bien hors service : un appel encore tenu ne
    // reviendra pas. C'est le SEUL cas où une erreur sans leg identifié touche
    // la machine à états.
    if (this.activeCall) this.endLiveCallOnError(error)
  }

  /**
   * REPRISE DU POSTE — un seul chemin, deux déclencheurs (réveil d'iPad,
   * erreur du Device). Jamais de minuterie : toujours un événement réel.
   *
   * Ordre voulu : micro d'abord (le seul point réellement perdu au réveil, et
   * la seule façon de savoir si une erreur micro est ENCORE vraie), puis
   * l'AudioContext, puis la présence Twilio.
   */
  private async recoverDevice(): Promise<void> {
    if (this.recovery) return this.recovery
    const epoch = this.epoch
    const isStale = () => this.epoch !== epoch

    const pending: Promise<void> = (async () => {
      // 0) Le poste n'a JAMAIS abouti (init en échec : micro refusé au
      //    démarrage, backend injoignable…). Il n'y a pas de session à
      //    réparer — il faut la créer. Sans ça, un agent dont le premier
      //    `init()` a échoué restait hors service jusqu'au rechargement.
      if (!this.device) {
        // ⚠️ `init()` SANS `askMicNow`, à dessein (recette #190, D8). Cette
        // branche est injoignable aujourd'hui : le réveil n'est écouté
        // qu'après une init réussie, `refreshToken` sort sans Device, et
        // `ensureReady` traite ce cas avant d'arriver ici. Si un futur
        // appelant y parvenait SANS geste de l'agent (une minuterie, un
        // événement réseau), la garde de permission doit jouer — il n'a pas à
        // faire surgir la demande du navigateur. Un appelant porteur d'un
        // geste passe par `ensureReady`, qui pose `askMicNow` lui-même.
        await this.init().catch((error) => {
          console.warn('[telephony-v2] Ré-initialisation du poste en échec', error)
        })
        return
      }

      // 1) Micro — sur iPad, Safari le coupe à la mise en veille.
      try {
        await this.warmMicrophone()
        if (isStale()) return
      } catch (error) {
        if (isStale()) return
        const typed = this.toDeviceError(error)
        console.warn('[telephony-v2] Micro non récupéré', typed)
        // Une erreur micro est TERMINALE jusqu'à une action de l'agent
        // (autoriser le micro) : ré-enregistrer la présence d'un poste muet
        // ferait sonner un agent incapable de parler.
        this.setStatus('error', typed)
        this.emit('error', typed)
        return
      }

      // 2) Audio de sortie (Safari suspend le contexte pendant la veille).
      this.getAudioContext()

      // 3) Présence Twilio.
      const device = this.device
      if (!device || isStale()) return
      try {
        // `this.status.state === 'error'` fait PARTIE de la condition : le SDK
        // peut se croire `registered` alors qu'il ne reçoit plus rien (cas
        // 31205, cf. `reRegister`). Se fier au seul état du SDK laissait le
        // poste sourd jusqu'au rechargement de la page.
        if (device.state !== 'registered' || this.status.state === 'error') {
          await this.reRegister(device)
          if (isStale()) return
        }
        this.setStatus('registered')
      } catch (error) {
        if (isStale()) return
        const typed = this.toDeviceError(error)
        console.error('[telephony-v2] Ré-enregistrement impossible', typed)
        this.setStatus('error', typed)
        this.emit('error', typed)
      }
    })().finally(() => {
      // Comme la garde d'init : ne relâcher que SA propre reprise, sinon un
      // `destroy()` suivi d'une nouvelle reprise verrait la sienne effacée par
      // la fin de la précédente.
      if (this.recovery === pending) this.recovery = null
    })

    this.recovery = pending
    return pending
  }

  /**
   * Ré-enregistre la PRÉSENCE du poste, y compris quand le SDK se croit encore
   * enregistré.
   *
   * Cas réel (31205 — jeton expiré sur un stream VIVANT) : le SDK arrête son
   * minuteur de présence mais laisse son état à `registered`. Le poste ne
   * reçoit alors plus aucun appel, sans le moindre signe, et `register()`
   * refuse de repartir (il exige l'état `unregistered`). Il faut donc passer
   * explicitement par `unregister()`.
   */
  private async reRegister(device: Device): Promise<void> {
    if (device.state === 'registered') {
      try {
        await device.unregister()
      } catch (error) {
        // Course avec un unregister/destroy concurrent : l'état visé est de
        // toute façon atteint, `registerTolerant` fait le reste.
        if (!isInvalidStateError(error)) throw error
      }
    }
    await this.registerTolerant(device)
  }

  /**
   * `register()` du SDK, TOLÉRANT à un enregistrement déjà en vol.
   *
   * Le SDK lève `InvalidStateError` si on appelle `register()` alors qu'il est
   * déjà en train de s'enregistrer. Ce n'est PAS un échec du poste : il suffit
   * d'attendre l'issue en cours. Le traiter comme une erreur condamnait à tort
   * un appel vivant (faux négatif de rétablissement).
   */
  private async registerTolerant(device: Device): Promise<void> {
    try {
      await device.register()
    } catch (error) {
      if (!isInvalidStateError(error)) throw error
      if (device.state === 'registered') return
      await this.awaitRegistered(device)
    }
  }

  /** Attente bornée de l'issue d'un enregistrement en vol (cf. constante). */
  private awaitRegistered(device: Device): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let settled = false
      const finish = (outcome: () => void) => {
        if (settled) return
        settled = true
        clearTimeout(guard)
        device.off('registered', onRegistered)
        device.off('unregistered', onUnregistered)
        outcome()
      }
      const onRegistered = () => finish(resolve)
      const onUnregistered = () =>
        finish(() => reject(new Error("L'enregistrement du poste n'a pas abouti")))
      const guard = setTimeout(
        () =>
          finish(() =>
            reject(new Error("L'enregistrement du poste n'a jamais répondu")),
          ),
        REGISTER_WAIT_TIMEOUT_MS,
      )
      device.on('registered', onRegistered)
      device.on('unregistered', onUnregistered)
    })
  }

  /**
   * Vérifie que le poste peut passer un appel, et le remet en service si la
   * session Twilio est tombée (veille prolongée, réseau perdu, jeton expiré
   * sur stream vivant). Sans minuterie : la reprise est déclenchée par une
   * action de l'agent, jamais par un retry en boucle.
   */
  async ensureReady(): Promise<void> {
    if (!this.device) {
      // Action de l'agent : c'est LE geste qui autorise à demander le micro
      // (issue #190) — on ne s'arrête pas en `awaiting-mic` ici.
      await this.init({ askMicNow: true })
      return
    }

    if (this.status.state === 'error') {
      // Une action de l'agent est LE moment de re-tester ce qui a échoué. La
      // reprise complète est la seule à savoir si l'erreur est encore vraie —
      // en particulier pour le micro : ré-enregistrer la présence sans
      // redemander le micro afficherait « Disponible » à un poste muet.
      await this.recoverDevice()
      // Statut RELU après la reprise : c'est elle qui vient de le décider.
      const recovered = this.getStatus()
      if (recovered.state !== 'registered') {
        throw new Error(
          recovered.error?.message ?? 'Poste téléphonique indisponible',
        )
      }
      return
    }

    if (this.device.state !== 'registered') {
      await this.reRegister(this.device)
      this.setStatus('registered')
    }
  }

  /**
   * Issue #190 — « Activer le micro » / « J'ai autorisé, réessayer ».
   *
   * À appeler DEPUIS UN CLIC : c'est ce geste qui rend la demande du
   * navigateur attendue au lieu de surprenante (et qui débloque l'audio de
   * Safari au passage). Délègue à `ensureReady` : poste jamais créé →
   * initialisation complète ; poste en erreur micro → reprise, qui redemande
   * le micro et ne rend `registered` que s'il est réellement retrouvé.
   *
   * Ne jette pas : l'issue est portée par le statut du poste (c'est lui que
   * l'écran affiche), exactement comme pour une initialisation au chargement.
   */
  async requestMicrophone(): Promise<void> {
    this.unlockAudio()
    try {
      // Une initialisation « au chargement » encore en vol (lecture de la
      // permission) s'arrêterait en `awaiting-mic` : on attend son verdict
      // pour que le clic ne soit pas absorbé par sa garde de concurrence.
      if (this.initializing) await this.initializing.catch(() => undefined)
      await this.ensureReady()
    } catch (error) {
      console.warn('[telephony-v2] Micro toujours indisponible après le geste', error)
    }
  }

  /** Ouvre la veille de permission une seule fois par époque (cf. `micWatch`). */
  private ensureMicWatch(): Promise<MicPermissionWatch> {
    if (this.micWatch) return this.micWatch
    const epoch = this.epoch
    const pending: Promise<MicPermissionWatch> = watchMicPermission((state) => {
      // Un changement arrivé après `destroy()` (ou pour une veille remplacée)
      // ne concerne plus ce poste.
      if (this.epoch !== epoch || this.micWatch !== pending) return
      this.onMicPermissionChange(state)
    })
    this.micWatch = pending
    return pending
  }

  /**
   * REPRISE AUTOMATIQUE (issue #190) : l'agent vient de modifier la permission
   * micro dans les réglages du site. Déclenchée par l'événement du navigateur
   * — jamais par une minuterie.
   *
   * N'agit QUE si le poste est bloqué par le micro : un poste sain qui voit
   * passer un `change` (Safari en émet au fil d'une session) n'est pas touché.
   */
  private onMicPermissionChange(state: MicPermissionState): void {
    const { state: deviceState, error } = this.status
    const blockedByMic =
      deviceState === 'awaiting-mic' || (deviceState === 'error' && error?.kind === 'mic')
    if (!blockedByMic) return

    if (state === 'granted') {
      console.warn('[telephony-v2] Micro autorisé — reprise du poste sans rechargement')
      if (this.device) {
        void this.recoverDevice()
      } else {
        // La permission est accordée : demander le micro ne peut plus rien
        // afficher, d'où `askMicNow` sans geste.
        void this.init({ askMicNow: true }).catch((initError) => {
          console.warn('[telephony-v2] Reprise après autorisation en échec', initError)
        })
      }
      return
    }

    // Le blocage a été LEVÉ sans être remplacé par une autorisation (« Demander »
    // dans les réglages du site) : le guide de sortie n'a plus lieu d'être, le
    // pré-écran « Activer le micro » reprend sa place.
    if (state === 'prompt' && !this.device && error?.mic === 'denied') {
      this.setStatus('awaiting-mic')
    }
  }

  destroy(): void {
    // Change d'époque AVANT tout : une initialisation encore en vol se
    // découvrira périmée à sa prochaine reprise et n'assignera rien.
    this.epoch += 1
    this.initializing = null
    // Une reprise en vol se découvrira périmée ; libérer le créneau permet à la
    // prochaine époque d'en lancer une propre sans attendre celle d'un mort.
    this.recovery = null

    this.ringback.stop()
    this.stopTokenRefresh()
    this.unbindVisibilityHandler()
    this.disarmReconnectWatchdog()

    // Veille de permission : désabonnée même si sa lecture est encore en vol.
    const micWatch = this.micWatch
    this.micWatch = null
    void micWatch?.then((watch) => watch.stop())

    this.activeCall?.disconnect()
    this.activeCall = null

    this.device?.destroy()
    this.device = null

    void this.audioContext?.close().catch(() => undefined)
    this.audioContext = null

    // Les sets d'abonnés ne sont PAS vidés : chaque abonné possède sa fonction
    // de désabonnement. Les vider ici couperait silencieusement l'UI si le
    // provider se remonte (StrictMode, changement d'utilisateur).
    this.setStatus('uninitialized')
  }

  // -------------------------------------------------------------------------
  // Token
  // -------------------------------------------------------------------------

  /**
   * Refresh PROACTIF : le SDK n'émet `tokenWillExpire` que ~30 s avant la fin,
   * et un iPad endormi à ce moment-là rate l'événement. On rafraîchit donc
   * 10 min avant expiration (v1 : 50 min pour un TTL de 60 min), en dérivant
   * l'intervalle du `ttl` réellement renvoyé par le backend.
   */
  private startTokenRefresh(ttlSeconds?: number): void {
    this.stopTokenRefresh()

    const ttl = ttlSeconds && ttlSeconds > 0 ? ttlSeconds : DEFAULT_TOKEN_TTL_SECONDS
    const intervalMs = Math.max(
      MIN_TOKEN_REFRESH_MS,
      (ttl - TOKEN_REFRESH_MARGIN_SECONDS) * 1000,
    )

    this.tokenRefreshTimer = setInterval(() => {
      void this.refreshToken()
    }, intervalMs)
  }

  private stopTokenRefresh(): void {
    if (this.tokenRefreshTimer) {
      clearInterval(this.tokenRefreshTimer)
      this.tokenRefreshTimer = null
    }
  }

  /**
   * Un échec de refresh n'est PAS suivi d'un retry programmé (interdit) : la
   * reprise passe par `recoverDevice`, déclenchée ici même par l'échec.
   *
   * ⚠️ Un jeton renouvelé NE SUFFIT PAS à remettre un poste en service. Sur
   * 31205 (jeton expiré alors que le stream vivait encore), le SDK a arrêté le
   * minuteur de présence : `updateToken()` réussit, l'agent paraît sain, et il
   * ne reçoit plus rien. Depuis un état d'erreur, on repasse donc par une
   * reprise complète — qui ré-enregistre la présence — plutôt que de se
   * contenter d'effacer le message d'erreur (ce que faisait l'ancien code, en
   * laissant `state` à `'error'` : la disponibilité n'était même pas
   * republiée).
   */
  private async refreshToken(): Promise<void> {
    if (!this.device) return
    const wasInError = this.status.state === 'error'
    try {
      const { token } = await telephonyApi.getToken()
      this.device.updateToken(token)
      this.emit('tokenRefreshed')
      if (wasInError) {
        // Le poste retrouve `registered` (et le provider republie la
        // disponibilité) seulement si la présence est réellement rétablie.
        await this.recoverDevice()
      } else if (this.status.error?.kind === 'token') {
        this.setStatus(this.status.state)
      }
    } catch (error) {
      console.error('[telephony-v2] Échec refresh token', error)
      const typed: TelephonyDeviceError = {
        kind: 'token',
        message: 'Session téléphonique expirée — reconnectez-vous',
      }
      this.setStatus('error', typed)
      this.emit('error', typed)
      // Le jeton n'a pas pu être renouvelé : tenter une reprise complète est la
      // seule issue automatique. Sans elle, le poste restait sourd jusqu'au
      // rechargement de la page.
      await this.recoverFromError(typed)
    }
  }

  // -------------------------------------------------------------------------
  // Audio (Safari) & micro
  // -------------------------------------------------------------------------

  /**
   * Safari refuse toute sortie audio tant qu'un AudioContext n'a pas été créé
   * ou repris DANS un geste utilisateur. À appeler depuis un handler de clic
   * (c'est déjà le cas de `connect()` et `acceptIncoming()`).
   */
  unlockAudio(): void {
    void this.getAudioContext()
  }

  private getAudioContext(): AudioContext | null {
    try {
      if (!this.audioContext) {
        const Ctor: typeof AudioContext =
          window.AudioContext ??
          (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
        if (!Ctor) return null
        this.audioContext = new Ctor()
      }
      if (this.audioContext.state === 'suspended') {
        void this.audioContext.resume().catch(() => undefined)
      }
      return this.audioContext
    } catch (error) {
      console.warn('[telephony-v2] AudioContext indisponible', error)
      return null
    }
  }

  /**
   * Demande (ou redemande) un flux micro puis le relâche immédiatement.
   * Sur iPad, Safari coupe le micro à la mise en veille : sans cette
   * ré-acquisition au réveil, l'agent parle dans le vide — bug iPad n°1.
   */
  private async warmMicrophone(): Promise<void> {
    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (error) {
      // Issue #190 — un REFUS efface le mémo « déjà autorisé sur cet
      // appareil » : au prochain chargement, Safari/iPad repassera par le
      // pré-écran au lieu de redemander par surprise. L'erreur, elle, remonte
      // INCHANGÉE (les appelants la typent et décident, comme avant).
      const name = (error as { name?: string } | null)?.name
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') forgetMicGrant()
      throw error
    }
    rememberMicGrant()
    stream.getTracks().forEach((track) => track.stop())
  }

  // -------------------------------------------------------------------------
  // Veille / réveil iPad
  // -------------------------------------------------------------------------

  private bindVisibilityHandler(): void {
    if (this.visibilityBound) return
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    this.visibilityBound = true
  }

  private unbindVisibilityHandler(): void {
    if (!this.visibilityBound) return
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    this.visibilityBound = false
    this.hiddenAt = null
  }

  private handleVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      this.hiddenAt = Date.now()
      return
    }
    void this.recoverFromWake()
  }

  /**
   * Reprise après réveil — délègue à `recoverDevice` (micro, audio, présence),
   * partagée avec la reprise sur erreur : deux reprises ne peuvent JAMAIS
   * s'enregistrer en même temps, et un enchaînement rapide de bascules
   * d'onglet ne déclenche pas N `getUserMedia` concurrents.
   *
   * ⚠️ La garde `!this.device` d'origine est retirée à dessein : un poste dont
   * l'initialisation a échoué (micro refusé au démarrage, backend injoignable)
   * était condamné jusqu'au rechargement, alors que le réveil est justement
   * l'occasion de réessayer. `recoverDevice` s'en charge.
   */
  private async recoverFromWake(): Promise<void> {
    const sleptMs = this.hiddenAt ? Date.now() - this.hiddenAt : 0
    this.hiddenAt = null

    // Un aller-retour d'onglet d'une seconde n'a rien cassé — sauf pendant un
    // appel, où la moindre coupure de micro est audible côté contact.
    if (sleptMs < WAKE_UP_THRESHOLD_MS && !this.activeCall) return

    return this.recoverDevice()
  }

  // -------------------------------------------------------------------------
  // Appels
  // -------------------------------------------------------------------------

  /** Appel sortant. Le Call DB existe déjà — callId circule en custom param. */
  async connect(params: { callId: string }): Promise<void> {
    if (this.activeCall) throw new Error('Un appel est déjà en cours')

    // Appelé depuis un clic : c'est LE moment où Safari autorise l'audio.
    this.unlockAudio()
    await this.ensureReady()
    if (!this.device) throw new Error('Device non initialisé')

    const context = this.getAudioContext()
    if (context) this.ringback.start(context)

    try {
      const call = await this.device.connect({
        params: { callId: params.callId },
      })
      this.activeCall = call
      this.bindCallEvents(call)
    } catch (error) {
      this.ringback.stop()
      throw error
    }
  }

  acceptIncoming(): void {
    this.unlockAudio()
    if (!this.activeCall) {
      // L'UI propose de décrocher un appel que le poste ne tient plus (leg
      // annulé pendant que l'agent tendait la main vers le bouton). Sans ce
      // rattrapage, l'écran resterait bloqué sur « Appel entrant ».
      console.warn('[telephony-v2] Décroché sans leg actif — état résiduel nettoyé')
      callMachine.reset()
      return
    }
    this.activeCall.accept()
  }

  rejectIncoming(): void {
    this.activeCall?.reject()
    this.releaseCall()
    callMachine.reset()
  }

  /**
   * RAPPEL SILENCIEUX du leg en vol — Stop / Pause pendant la fenêtre de
   * connexion anticipée (cf. `prelaunch.ts`, constat C du 2026-07-29).
   *
   * « Silencieux » a un sens précis : les écouteurs du leg sont retirés AVANT
   * de le raccrocher, de sorte que son `disconnect` ne pousse PAS la machine
   * en `wrapup`. C'est exactement la différence avec `hangup()` — ici il n'y a
   * rien à qualifier, personne n'a sonné, et l'agent doit retrouver la préview
   * du contact qu'il vient de retenir, pas un formulaire de résultat.
   *
   * L'ordre (retirer, puis raccrocher) est le contrat : raccrocher d'abord
   * laisserait le `disconnect` du SDK atteindre la machine avant qu'on ne
   * l'ait détachée — c'est très précisément le chemin qui a produit le bug du
   * panneau invisible (constat E), dans l'autre sens.
   *
   * @returns `false` si le poste ne tenait aucun leg — l'annulation est alors
   *          purement logique (le `Call` reste à nettoyer côté serveur).
   */
  abortDialing(): boolean {
    const call = this.activeCall
    if (!call) {
      console.warn('[telephony-v2] Rappel de leg sans leg actif — rien à raccrocher')
      return false
    }
    // Détache d'abord (retire les écouteurs, coupe la sonnerie locale, rend le
    // poste libre), raccroche ensuite : aucun événement de ce leg ne peut plus
    // toucher la machine.
    this.releaseCall(call)
    try {
      call.disconnect()
    } catch (error) {
      // Le leg était déjà mort : rien à raccrocher, l'état local est propre.
      console.warn('[telephony-v2] Raccrochage du leg rappelé impossible', error)
    }
    return true
  }

  hangup(): void {
    const call = this.activeCall
    if (!call) {
      // Même raison qu'`acceptIncoming` : un bouton « Raccrocher » qui ne fait
      // rien laisse l'agent coincé, et le coince pour tous les appels suivants.
      console.warn('[telephony-v2] Raccrochage sans leg actif — état résiduel nettoyé')
      callMachine.disconnected()
      return
    }
    /**
     * ⚠️ B-01 — LE LEG EST DÉJÀ `closed` SANS QUE LE SDK L'AIT DIT.
     *
     * Après une coupure de signalisation sans jeton de reconnexion, le SDK a
     * passé le leg en `Closed` SANS émettre `disconnect` (`call.js:499-513`),
     * et `disconnect()` y est un no-op (`_disconnect`, `call.js:1217-1224`).
     * Le bouton « Raccrocher » ne faisait donc RIEN : le poste restait « en
     * appel » jusqu'au rechargement de la page. On exécute le nettoyage de fin
     * d'appel nous-mêmes — idempotent avec le chemin `transportClose`.
     */
    if (this.callStatus(call) === CALL_STATUS_CLOSED) {
      this.endCallOnSignalingLoss(call, 'hangup')
      return
    }
    call.disconnect()
  }

  setMuted(muted: boolean): void {
    this.activeCall?.mute(muted)
    callMachine.setMuted(muted)
  }

  sendDigits(digits: string): void {
    this.activeCall?.sendDigits(digits)
  }

  /**
   * VOLUME D'ÉCOUTE du correspondant (0 → 1).
   *
   * Wrapper mince, et deux propriétés qui comptent :
   *
   *  - **il s'applique tout de suite** aux éléments de lecture de l'appel en
   *    cours, s'il y en a un ;
   *  - **il PERSISTE** : la valeur est retenue et ré-appliquée à chaque
   *    nouvel élément audio (donc à chaque appel suivant), sans quoi l'agent
   *    devrait refaire son réglage entre deux contacts.
   *
   * Aucune erreur possible côté agent : la valeur est bornée ici.
   */
  setVolume(level: number): void {
    const normalized = Math.max(0, Math.min(1, Number.isFinite(level) ? level : 1))
    if (this.outputVolume === normalized) return
    this.outputVolume = normalized
    this.remoteAudio.forEach((element) => {
      try {
        element.volume = normalized
      } catch (error) {
        console.warn('[telephony-v2] Volume non appliqué', error)
      }
    })
    // Le contrôle s'abonne à cet événement — la scène, elle, ne bouge pas.
    this.emit('volume', normalized)
  }

  /** Volume d'écoute courant (0 → 1) — lecture pour l'UI. */
  getVolume(): number {
    return this.outputVolume
  }

  /** Diagnostic (page Telephony Lab v2) — lecture seule, aucun effet de bord. */
  getDiagnostics(): {
    status: TelephonyDeviceStatus
    deviceState: string
    hasActiveCall: boolean
    callSid: string | null
    ringbackPlaying: boolean
    audioContextState: string | null
    subscribers: Record<TelephonyDeviceEvent, number>
  } {
    return {
      status: this.status,
      deviceState: this.device?.state ?? 'uninitialized',
      hasActiveCall: this.activeCall !== null,
      callSid: this.activeCall?.parameters?.CallSid ?? null,
      ringbackPlaying: this.ringback.isPlaying,
      audioContextState: this.audioContext?.state ?? null,
      subscribers: {
        volume: this.subscribers.volume.size,
        incoming: this.subscribers.incoming.size,
        accept: this.subscribers.accept.size,
        disconnect: this.subscribers.disconnect.size,
        reconnecting: this.subscribers.reconnecting.size,
        reconnected: this.subscribers.reconnected.size,
        error: this.subscribers.error.size,
        status: this.subscribers.status.size,
        tokenRefreshed: this.subscribers.tokenRefreshed.size,
      },
    }
  }

  /**
   * Fin d'appel : la sonnerie locale s'arrête et les écouteurs de CE Call sont
   * retirés. Les objets Call sont jetables ; sans ce retrait, un agent qui
   * enchaîne 200 appels accumule 200 jeux d'écouteurs.
   */
  private releaseCall(call?: Call): void {
    this.ringback.stop()
    // Un événement TARDIF d'un leg déjà relâché (une erreur de démontage, par
    // exemple) ne doit pas relâcher le leg SUIVANT : sans ce recoupement, un
    // entrant tout juste accepté se retrouverait sans référence, donc
    // impossible à raccrocher (famille de bugs F-8).
    if (call && this.activeCall !== call) {
      call.removeAllListeners()
      return
    }
    // Le leg actif s'en va : sa reconnexion éventuelle n'a plus rien à garder.
    this.disarmReconnectWatchdog()
    this.activeCall?.removeAllListeners()
    this.activeCall = null
    // Les éléments de lecture appartenaient à CE leg : les garder ferait
    // grossir le set d'un élément mort par appel (200 par journée d'agent).
    // Le volume, lui, est retenu — c'est un réglage de l'agent, pas de l'appel.
    this.remoteAudio.clear()
  }

  private bindCallEvents(call: Call): void {
    /**
     * Élément de lecture du flux DISTANT, fourni par le SDK (événement public
     * `audio`). C'est le seul point où le volume d'écoute se règle réellement
     * — cf. la note sur `remoteAudio` : le `connection.volume()` de la v1
     * n'existe pas dans le SDK 2.x.
     *
     * Le réglage courant lui est appliqué DÈS son arrivée : l'agent qui a
     * baissé le son au contact précédent le retrouve baissé sur celui-ci.
     */
    call.on('audio', (remoteAudio: HTMLAudioElement) => {
      this.remoteAudio.add(remoteAudio)
      try {
        remoteAudio.volume = this.outputVolume
      } catch (error) {
        console.warn('[telephony-v2] Volume initial non appliqué', error)
      }
    })
    // Dès que Twilio a du signal à faire entendre, la tonalité locale s'efface.
    call.on('ringing', () => {
      this.ringback.stop()
      callMachine.remoteRinging()
    })
    /**
     * SUPERPOSITION SONORE — « tonalité bizarre » sur les mobiles FRANÇAIS,
     * jamais sur les israéliens (constat D de la recette du 2026-07-29).
     *
     * La tonalité locale ne s'arrêtait que sur `ringing` et `accept`. Or les
     * opérateurs français envoient de l'**early media** : leur propre retour
     * d'appel (ou une annonce) circule dans le flux audio AVANT tout décroché,
     * et parfois avant même que le signal `ringing` ne remonte. Deux sonneries
     * jouaient alors ensemble, à des cadences différentes — d'où le son
     * « bizarre ». Les réseaux sans early media (IL) n'exposaient rien.
     *
     * Le SDK expose l'information de deux façons : `ringing(hasEarlyMedia)` et,
     * surtout, `volume(input, output)` — `output` est le niveau du flux
     * DISTANT. Il n'existe pas de « peut-être » : dès qu'on entend quelque
     * chose venant d'en face, notre propre tonalité n'a plus de raison d'être.
     *
     * Le seuil n'est pas zéro : un flux ouvert mais silencieux produit un
     * plancher de bruit non nul. `REMOTE_AUDIO_VOLUME_THRESHOLD` est bas
     * (au-dessus du plancher, très en dessous d'une voix ou d'une tonalité).
     *
     * L'abonnement reste attaché tout l'appel — `stop()` est idempotent et ne
     * coûte rien une fois la tonalité éteinte.
     */
    call.on('volume', (_inputVolume: number, outputVolume: number) => {
      if (!this.ringback.isPlaying) return
      if (outputVolume > REMOTE_AUDIO_VOLUME_THRESHOLD) {
        this.ringback.stop()
      }
    })
    call.on('accept', () => {
      this.ringback.stop()
      callMachine.connected()
      this.emit('accept', call)
    })
    call.on('disconnect', () => {
      const wasLive = this.activeCall === call
      this.releaseCall(call)
      if (wasLive) callMachine.disconnected({ needsOutcome: true })
      this.emit('disconnect', call)
    })
    call.on('cancel', () => {
      // Entrant annulé par l'appelant, ou décroché par un AUTRE agent : rien à
      // qualifier ici, le poste redevient immédiatement disponible.
      const wasLive = this.activeCall === call
      this.releaseCall(call)
      if (wasLive) callMachine.disconnected({ needsOutcome: false })
      this.emit('disconnect', call)
    })
    call.on('reject', () => {
      this.releaseCall(call)
    })
    /**
     * ⚠️ B-01 — « POSTE ZOMBIE » : coupure de signalisation en plein appel.
     *
     * Vérifié dans le SDK 2.16 (`call.js:499-513`) : sur `transportClose` du
     * pstream, le leg émet `transportClose` puis, S'IL a un jeton de
     * reconnexion → `Reconnecting` (et `reconnecting` est émis) ; SINON →
     * `_status = Closed` **sans émettre `disconnect`**. `_disconnect()` devient
     * alors un no-op, et le Device ne retire le leg que s'il était encore
     * `Pending` (`device.js:1159`). Résultat côté wrapper : `activeCall` restait
     * posé, `hangup()` ne faisait rien, `connect()` jetait « Un appel est déjà
     * en cours » — jusqu'au rechargement (test utilisateur : wifi coupé,
     * « l'appel était actif mais le son ne passait plus »).
     *
     * ⚠️ ORDRE DU SDK : `emit('transportClose')` PRÉCÈDE l'affectation de
     * `_status`. Dans ce handler, `status()` ment encore (il dit `open`). Le
     * verdict se lit donc APRÈS l'événement, à la microtâche suivante : le SDK
     * a alors tranché entre `closed` (fin d'appel) et `reconnecting` (garde).
     */
    call.on('transportClose', () => {
      queueMicrotask(() => this.onTransportClosed(call))
    })
    call.on('reconnecting', (error?: { code?: number; message?: string }) => {
      if (this.activeCall !== call) return
      console.warn('[telephony-v2] Appel en reconnexion', {
        sid: call.parameters?.CallSid,
        code: error?.code,
        message: error?.message,
      })
      this.armReconnectWatchdog(call)
      this.emit('reconnecting', call)
    })
    call.on('reconnected', () => {
      if (this.activeCall !== call) return
      console.warn('[telephony-v2] Appel rétabli après reconnexion', {
        sid: call.parameters?.CallSid,
      })
      this.disarmReconnectWatchdog()
      this.emit('reconnected', call)
    })
    call.on('error', (error) => {
      const typed = this.toDeviceError(error)
      console.error('[telephony-v2] Call error', typed)
      // Une erreur qui arrive APRÈS la fin du leg (démontage) ne concerne plus
      // un appel vivant : la signaler suffit. La faire remonter jusqu'à
      // `fail()` effacerait le wrapup en cours de saisie — ou, pire, l'entrant
      // qui vient d'être présenté à l'agent (constat F-8).
      const wasLive = this.activeCall === call
      /**
       * ⚠️ LE BANDEAU DU POSTE NE REFLÈTE QUE LE POSTE.
       *
       * Ce handler écrivait TOUTE erreur de leg dans `status.error` — donc la
       * scène affichait « Connexion au service téléphonique perdue » pour un
       * numéro occupé, sur un Device parfaitement enregistré. Seules les
       * erreurs qui désignent réellement le poste (micro, jeton) y entrent
       * désormais ; les autres sont des résultats d'appel.
       */
      if (isDeviceScopedError(typed.kind)) this.setStatus(this.status.state, typed)
      this.emit('error', typed)
      if (wasLive) {
        // ⚠️ L'ORDRE compte : `endLiveCallOnError` raccroche le leg AVANT de le
        // relâcher. Relâcher d'abord (ce que faisait ce handler) lui retirait
        // toute référence à raccrocher — si le média survivait à l'erreur de
        // signalisation, l'appel Twilio continuait sans aucune UI pour y mettre
        // fin.
        //
        // Un appel COUPÉ par une erreur reste par ailleurs un appel qui a eu
        // lieu : en conversation, il part en wrapup et demeure qualifiable
        // (`fail()` sec le renvoyait à `idle` en perdant le résultat, tout en
        // laissant le backend exclure l'agent pendant sa fenêtre de wrapup).
        //
        // `legFailure` : une erreur de transport SUR CE LEG avant décroché est
        // un échec de numérotation (cf. `endLiveCallOnError`, qui arbitre selon
        // la phase). Une erreur micro/jeton, elle, garde le chemin nominal.
        this.endLiveCallOnError(typed, { legFailure: !isDeviceScopedError(typed.kind) })
      } else {
        // Leg déjà mort : rien à raccrocher, seuls ses écouteurs sont retirés.
        this.releaseCall(call)
      }
    })
  }

  // -------------------------------------------------------------------------
  // Coupure de signalisation (B-01)
  // -------------------------------------------------------------------------

  /** `call.status()` en chaîne (cf. `CALL_STATUS_CLOSED`). */
  private callStatus(call: Call): string {
    return call.status()
  }

  /**
   * Verdict APRÈS `transportClose` (cf. le handler) : le SDK a maintenant posé
   * l'état du leg.
   */
  private onTransportClosed(call: Call): void {
    // `disconnect`/`cancel` ont pu passer dans la même tick : rien à faire.
    if (this.activeCall !== call) return
    const status = this.callStatus(call)
    if (status === CALL_STATUS_CLOSED) {
      this.endCallOnSignalingLoss(call, 'transport-close')
      return
    }
    if (status === CALL_STATUS_RECONNECTING) {
      // Normalement déjà armé par `reconnecting` (émis dans la même pile que
      // `transportClose`) ; idempotent.
      this.armReconnectWatchdog(call)
    }
  }

  private armReconnectWatchdog(call: Call): void {
    if (this.reconnectWatchdog) return
    this.reconnectWatchdog = setTimeout(() => {
      this.reconnectWatchdog = null
      if (this.activeCall !== call) return
      console.error(
        '[telephony-v2] Reconnexion jamais aboutie — fin d’appel forcée',
        { sid: call.parameters?.CallSid, afterMs: CALL_RECONNECT_WATCHDOG_MS },
      )
      this.endCallOnSignalingLoss(call, 'watchdog')
    }, CALL_RECONNECT_WATCHDOG_MS)
  }

  private disarmReconnectWatchdog(): void {
    if (!this.reconnectWatchdog) return
    clearTimeout(this.reconnectWatchdog)
    this.reconnectWatchdog = null
  }

  /**
   * FIN D'APPEL PROPRE quand le SDK ne la prononcera pas lui-même.
   *
   * Trois déclencheurs, un seul chemin : `transportClose` sans jeton de
   * reconnexion (leg `closed`), chien de garde d'une reconnexion qui n'aboutit
   * pas, et `hangup()` sur un leg déjà `closed`.
   *
   * Ordre : on tente d'abord le raccrochage SDK — s'il est encore possible
   * (leg `reconnecting`), le SDK émet `disconnect` SYNCHRONEMENT et le handler
   * nominal fait tout (relâche, machine, abonnés) ; on s'arrête là. Sinon (leg
   * `closed`, `disconnect()` no-op), on refait à la main EXACTEMENT ce que fait
   * le handler `disconnect` : relâche du leg, machine, diffusion aux abonnés.
   *
   * Idempotent : gardé par `this.activeCall === call`, et `releaseCall` retire
   * les écouteurs du leg — un `disconnect` tardif du SDK ne repasse donc plus
   * par ici ni par le handler nominal (un seul nettoyage, une seule diffusion).
   *
   * Machine : conversation établie → `wrapup` qualifiable (l'appel a eu lieu),
   * avec la cause affichable ; sinon → échec, au repos avec l'erreur (même
   * arbitrage que `endLiveCallOnError` pour une erreur de poste).
   */
  private endCallOnSignalingLoss(
    call: Call,
    cause: 'transport-close' | 'watchdog' | 'hangup',
  ): void {
    if (this.activeCall !== call) return
    this.disarmReconnectWatchdog()
    const { phase, callId } = callMachine.getSnapshot()
    console.warn('[telephony-v2] Fin d’appel sur coupure de signalisation', {
      cause,
      phase,
      sid: call.parameters?.CallSid,
      status: this.callStatus(call),
    })

    try {
      call.disconnect()
    } catch (error) {
      console.warn('[telephony-v2] Raccrochage du leg coupé impossible', error)
    }
    // Le SDK a pu raccrocher (leg `reconnecting`) : son `disconnect` synchrone
    // vient de tout nettoyer par le chemin nominal.
    if (this.activeCall !== call) return

    this.releaseCall(call)
    const errorKey = 'device.error.network'
    if (phase === 'active' && callId) {
      callMachine.disconnected({ needsOutcome: true, error: errorKey })
    } else {
      callMachine.fail(errorKey)
    }
    this.emit('disconnect', call)
  }

  // -------------------------------------------------------------------------
  // Typage des erreurs
  // -------------------------------------------------------------------------

  /**
   * Traduit une erreur SDK / DOM en famille exploitable par l'UI.
   * Un refus de micro remonte tantôt en `NotAllowedError` (getUserMedia),
   * tantôt en 31201/31208 (ré-emballé par le SDK) : les deux formes sont
   * testées plutôt que d'en supposer une.
   */
  private toDeviceError(error: unknown): TelephonyDeviceError {
    const raw = (error ?? {}) as { code?: number; name?: string; message?: string }
    const code = typeof raw.code === 'number' ? raw.code : undefined
    const message = raw.message ?? 'Erreur téléphonie'

    // Issue #190 — trois causes, trois gestes : `mic` les distingue (les
    // messages restent distincts eux aussi : `setStatus` déduplique sur eux).
    if (raw.name === 'NotAllowedError' || raw.name === 'PermissionDeniedError') {
      return {
        kind: 'mic',
        mic: 'denied',
        code,
        message: 'Micro refusé — autorisez le microphone',
      }
    }
    if (raw.name === 'NotFoundError' || raw.name === 'DevicesNotFoundError') {
      return { kind: 'mic', mic: 'not-found', code, message: 'Aucun micro détecté' }
    }
    if (raw.name === 'NotReadableError' || raw.name === 'TrackStartError') {
      return {
        kind: 'mic',
        mic: 'busy',
        code,
        message: 'Micro utilisé par une autre application',
      }
    }
    if (code !== undefined && MIC_ERROR_CODES.has(code)) {
      return {
        kind: 'mic',
        mic: 'unavailable',
        code,
        message: 'Micro indisponible — vérifiez les autorisations',
      }
    }
    if (code !== undefined && TOKEN_ERROR_CODES.has(code)) {
      return { kind: 'token', code, message: 'Session téléphonique expirée' }
    }
    if (code !== undefined && code >= 31000 && code < 31100) {
      return { kind: 'network', code, message: message || 'Connexion Twilio perdue' }
    }
    return { kind: 'unknown', code, message }
  }
}

/** Singleton — un seul Device Twilio par session (sinon double sonnerie). */
export const telephonyDevice = new TelephonyDevice()
